import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  useToast,
  ButtonGroup,
  HStack,
  Badge,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Icon,
  useDisclosure,
  Divider,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FaBars, FaUser, FaCrown, FaCoins, FaExchangeAlt, FaShieldAlt } from 'react-icons/fa';
import EmailVerifyNotification from './auth/EmailVerifyNotification';
import { useIsMutating } from 'react-query';

const Header = () => {
  const {
    currentUser: user,
    logout,
    loading: authLoading,
    resendVerification,
    isLoadingResend,
  } = useAuth();
  const loginMutations = useIsMutating({ mutationKey: ['googleLogin'] });

  const loading = authLoading || loginMutations > 0;

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [resend, setResend] = useState(false);

  const isUserEmailVerified = user && !!user.is_verified;
  const subscription = user && user.subscription_type;
  const isAdmin = user && !!user.admin;

  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: 'Success',
        description: 'Successfully logged out',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to log out',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onResendVerification = () => {
    setResend(true);
    resendVerification();
  };

  return (
    <VStack gap={0} spacing={0} width={"100%"} display={"flex"} align='stretch'>
      {user && !loading && !isUserEmailVerified && (
        <EmailVerifyNotification
          onClickResend={onResendVerification}
          isSent={!!resend}
          loading={isLoadingResend}
        />
      )}
      <Box bg='white' px={4} py={3} shadow='md'>
        <Flex justify='space-between' align='center' maxW='1200px' mx='auto'>
          <HStack spacing={4}>
            {user && (
              <IconButton
                icon={<FaBars />}
                variant='ghost'
                color='black'
                onClick={onOpen}
                _hover={{ bg: 'blackAlpha.200' }}
                aria-label='Menu'
              />
            )}
            <Link to='/' style={{ textDecoration: 'none' }}>
              <HStack spacing={2}>
                <Box height="40px">
                  <img src="/images/dwelz-logo.png" alt="Dwelz Logo" style={{ height: '100%' }} />
                </Box>
                <Text
                  color='black'
                  fontSize='xl'
                  fontWeight='light'
                  cursor='pointer'
                  _hover={{ opacity: 0.9 }}
                >
                  dwelz
                </Text>
              </HStack>
            </Link>
          </HStack>

          <Drawer placement='left' onClose={onClose} isOpen={isOpen}>
            <DrawerOverlay />
            <DrawerContent bg='white'>
              <DrawerCloseButton />
              <DrawerHeader borderBottomWidth='1px'>
                <Text color='black' fontWeight='bold'>
                  Menu
                </Text>
              </DrawerHeader>
              <DrawerBody>
                {user && (
                  <HStack spacing={2} align={'stretch'} mb={5}>
                    <Text color='black'>Welcome, {user.email}</Text>
                  </HStack>
                )}
                <VStack spacing={4} align='stretch'>
                  <Button
                    leftIcon={<Icon as={FaUser} />}
                    variant='ghost'
                    justifyContent='flex-start'
                    onClick={() => {
                      onClose();
                      navigate('/account');
                    }}
                  >
                    My Account
                  </Button>

                  <Divider />

                  <VStack align='stretch' spacing={2}>
                    <HStack>
                      <Icon as={FaCrown} color='black' />
                      <Text fontWeight='medium'>Subscription</Text>
                    </HStack>
                    <Badge
                      alignSelf='flex-start'
                      colorScheme={
                        subscription === 'premium'
                          ? 'purple'
                          : subscription === 'standard'
                            ? 'blue'
                            : 'gray'
                      }
                      px={2}
                      py={1}
                      textTransform='capitalize'
                    >
                      {subscription || 'Free'}
                    </Badge>
                    <Button
                      leftIcon={<Icon as={FaExchangeAlt} />}
                      size='sm'
                      variant='outline'
                      onClick={() => {
                        onClose();
                        navigate('/subscription');
                      }}
                    >
                      Change Plan
                    </Button>
                  </VStack>

                  <Divider />

                  <VStack align='stretch' spacing={2}>
                    <HStack>
                      <Icon as={FaCoins} color='black' />
                      <Text fontWeight='medium'>Credits</Text>
                    </HStack>
                    <Text fontSize='xl' fontWeight='bold' color='black'>
                      {user?.credits || 0}
                    </Text>
                    <Text fontSize='sm' color='black'>
                      Available for premium searches
                    </Text>
                  </VStack>

                  {isAdmin && (
                    <>
                      <Divider />
                      <Button
                        leftIcon={<Icon as={FaShieldAlt} />}
                        variant='ghost'
                        justifyContent='flex-start'
                        onClick={() => {
                          onClose();
                          navigate('/admin');
                        }}
                      >
                        Admin Dashboard
                      </Button>
                    </>
                  )}
                </VStack>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
          <Flex align='center'>
            {user ? (
              <>
                <HStack spacing={{ base: 2, sm: 2, md: 4 }}>
                  <Box display={{ base: "none", sm: "flex" }} columnGap={4} alignItems={"center"} justifyItems={"center"}>
                    <Text color='black'>Welcome, {user.email}</Text>
                    {subscription && (
                      <Badge
                        colorScheme={
                          subscription === 'premium'
                            ? 'purple'
                            : subscription === 'standard'
                              ? 'blue'
                              : 'gray'
                        }
                        variant='solid'
                        fontSize='xs'
                        textTransform='capitalize'
                      >
                        {subscription}
                      </Badge>
                    )}
                  </Box>
                  <Button
                    colorScheme='whiteAlpha'
                    variant='outline'
                    size='sm'
                    onClick={handleLogout}
                    color='black'
                  >
                    Logout
                  </Button>
                </HStack>
              </>
            ) : (
              <ButtonGroup spacing={4}>
                <Button
                  as={Link}
                  disabled={loading}
                  to='/login'
                  colorScheme='whiteAlpha'
                  variant='outline'
                  size='sm'
                  color='black'
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
                <Button as={Link} to='/register' colorScheme='whiteAlpha' size='sm'>
                  Register
                </Button>
              </ButtonGroup>
            )}
          </Flex>
        </Flex>
      </Box>
    </VStack>
  );
};

export default Header;
